import { createPrioritizationProjection } from '@portfolio-pm/projection';
import type {
  DaypartT,
  EnergyT,
  Project,
  Task,
  TaskActivity,
  WorkflowColumn,
} from '@portfolio-pm/shared';
import {
  CalendarPlus,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  ListTree,
  Maximize2,
  MoreVertical,
  Pencil,
  Plus,
  Repeat2,
  Tags,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import {
  type FormEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getDocStore } from '../../store/doc-store.ts';
import { ScrollViewport } from '../layout/ScrollViewport.tsx';
import { TaskActivityThread } from './detail/TaskActivityThread.tsx';
import { TaskAuditHistory } from './detail/TaskAuditHistory.tsx';
import { TaskDetailEditPanel } from './detail/TaskDetailEditPanel.tsx';
import { TaskPlanningData } from './detail/TaskPlanningData.tsx';
import { TaskViewSummaryPanel } from './detail/TaskViewSummaryPanel.tsx';
import { commitOnEnter, formatTaskAnchorLabel, getPriorityAccent } from './detail/detail-utils.ts';
import {
  ChipSelectField,
  ProjectSearchField,
  SmartDateField,
  SmartEstimateField,
  SmartRepeatField,
  TagsMultiSelectField,
  UrlField,
  WorkflowSelectField,
} from './detail/fields.tsx';
import type { TaskWorkspaceView } from './task-workspace-model.ts';

export function TaskDetailPanel({
  projects,
  workflowColumns,
  selectedTask,
  summaryTasks,
  summaryCompletedCount,
  allTasks,
  taskActivities,
  projectsById,
  titleFieldRef,
  currentView,
  viewTitle,
  viewDescription,
  onChangeView,
  onSelectChildTask,
  onOpenNotesWorkspace,
  onDeleteTask,
  onClose,
}: {
  projects: Project[];
  workflowColumns: WorkflowColumn[];
  selectedTask: Task | undefined;
  summaryTasks: Task[];
  summaryCompletedCount: number;
  allTasks: Task[];
  taskActivities: TaskActivity[];
  projectsById: Map<string, Project>;
  titleFieldRef: RefObject<HTMLInputElement | HTMLTextAreaElement>;
  currentView: TaskWorkspaceView;
  viewTitle: string;
  viewDescription: string;
  onChangeView(view: TaskWorkspaceView): void;
  onSelectChildTask?(taskId: string): void;
  onOpenNotesWorkspace?(taskId: string): void;
  onDeleteTask?(taskId: string): void;
  onClose?(): void;
}): JSX.Element {
  const store = getDocStore();
  const snapshot = store.useFocusSnapshot();
  const activities = store.useTaskActivities(selectedTask?.id);
  const prioritization = useMemo(
    () => createPrioritizationProjection(store.doc, store.projection),
    [store],
  );
  useEffect(() => () => prioritization.destroy(), [prioritization]);
  useSyncExternalStore(
    prioritization.subscribe,
    prioritization.getRevision,
    prioritization.getRevision,
  );
  const [childTitle, setChildTitle] = useState('');
  const childInputRef = useRef<HTMLInputElement>(null);
  const [activityNote, setActivityNote] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [actionsOpen, setActionsOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [subtaskTab, setSubtaskTab] = useState<'incomplete' | 'completed'>('incomplete');
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [optionalFields, setOptionalFields] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (selectedTask?.energy !== undefined) initial.add('energy');
    if (selectedTask?.daypart !== undefined) initial.add('daypart');
    return initial;
  });
  const selectedTaskId = selectedTask?.id;

  const appendChildToken = (token: string): void => {
    setChildTitle((current) => {
      const trimmed = current.trimEnd();
      return trimmed ? `${trimmed} ${token}` : token;
    });
    childInputRef.current?.focus();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset panel state only when switching tasks, not on task field edits
  useEffect(() => {
    if (selectedTaskId === undefined) {
      setEditing(false);
      setEditingNotes(false);
      setActionsOpen(false);
      return;
    }
    setEditing(false);
    setEditingNotes(false);
    setNotesDraft(selectedTask?.notes ?? '');
    setActionsOpen(false);
    setSubtaskTab('incomplete');
    setAddFieldOpen(false);
    setOptionalFields(() => {
      const s = new Set<string>();
      if (selectedTask?.energy !== undefined) s.add('energy');
      if (selectedTask?.daypart !== undefined) s.add('daypart');
      return s;
    });
  }, [selectedTaskId]);

  const allTagsWithCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const task of snapshot.tasks) {
      for (const tag of task.tags) {
        counts.set(tag, (counts.get(tag) ?? 0) + 1);
      }
    }
    return [...counts.entries()]
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
  }, [snapshot.tasks]);

  const childTasks = useMemo(
    () =>
      selectedTask
        ? snapshot.tasks
            .filter((task) => task.parentTaskId === selectedTask.id)
            .sort((a, b) => a.createdAt - b.createdAt)
        : [],
    [selectedTask, snapshot.tasks],
  );

  if (!selectedTask) {
    return (
      <TaskViewSummaryPanel
        tasks={summaryTasks}
        completedCount={summaryCompletedCount}
        allTasks={allTasks}
        taskActivities={taskActivities}
        projectsById={projectsById}
        title={viewTitle}
        description={viewDescription}
      />
    );
  }

  const dueFieldId = `${selectedTask.id}-due-field`;
  const scheduledFieldId = `${selectedTask.id}-scheduled-field`;
  const titleFieldId = `${selectedTask.id}-title-field`;
  const notesFieldId = `${selectedTask.id}-notes-field`;
  const hasNotes = Boolean(selectedTask.notes?.trim());
  const priorityColor = getPriorityAccent(selectedTask.priority);
  const visibleActivities = activities.filter((activity) => activity.type === 'comment');
  const taskEvidence = prioritization
    .getFieldEvidence()
    .filter((item) => item.entityType === 'task' && item.entityId === selectedTask.id);
  const taskEnrichmentEvents = prioritization
    .getEnrichmentEvents()
    .filter((item) => item.entityType === 'task' && item.entityId === selectedTask.id);
  const incompleteChildTasks = childTasks.filter((child) => child.completedAt === undefined);
  const completedChildTasks = childTasks.filter((child) => child.completedAt !== undefined);
  const visibleChildTasks = subtaskTab === 'completed' ? completedChildTasks : incompleteChildTasks;
  const saveNotes = (): void => {
    store.updateTask(selectedTask.id, { notes: notesDraft.trim() || undefined });
    setEditingNotes(false);
  };

  if (editing) {
    return (
      <TaskDetailEditPanel
        projects={projects}
        workflowColumns={workflowColumns}
        selectedTask={selectedTask}
        titleFieldRef={titleFieldRef}
        currentView={currentView}
        onChangeView={onChangeView}
        onClose={() => setEditing(false)}
      />
    );
  }

  return (
    <aside
      data-testid="task-detail-panel"
      className="relative flex h-full min-h-0 flex-col overflow-hidden bg-[color:var(--app-workspace)] text-[color:var(--app-text)]"
    >
      <section
        data-testid="task-detail-primary-fields"
        className="task-detail-primary-fields shrink-0 overflow-x-hidden px-3 pb-5 pt-6"
      >
        <div className="grid grid-cols-[2rem_minmax(0,1fr)_auto] items-start gap-2">
          <span
            aria-label={
              selectedTask.priority === 0 ? 'No priority' : `Priority ${selectedTask.priority}`
            }
            className="mt-1 inline-flex h-6 shrink-0 items-center justify-center self-start rounded-full px-1.5 text-xs font-medium text-white"
            style={{ backgroundColor: priorityColor }}
          >
            {selectedTask.priority === 0 ? '—' : `P${selectedTask.priority}`}
          </span>
          <label htmlFor={titleFieldId} className="sr-only">
            Title
          </label>
          <textarea
            id={titleFieldId}
            key={`${selectedTask.id}-title`}
            ref={titleFieldRef as RefObject<HTMLTextAreaElement>}
            rows={1}
            defaultValue={selectedTask.title}
            onBlur={(event) => store.renameTask(selectedTask.id, event.target.value)}
            onKeyDown={commitOnEnter}
            className="block min-w-0 w-full resize-none overflow-hidden break-words border-0 bg-transparent px-0 py-0 text-lg font-normal leading-8 [field-sizing:content] [overflow-wrap:anywhere] text-[color:var(--app-text)] outline-none focus:bg-[color:var(--app-input-bg)]"
          />
          <div className="relative flex items-center gap-1 pt-0.5 text-[color:var(--app-text-muted)]">
            <button
              type="button"
              aria-label={selectedTask.completedAt ? 'Reopen task' : 'Complete task'}
              onClick={() => store.toggleTaskComplete(selectedTask.id)}
              className="flex h-6 w-6 items-center justify-center rounded text-[color:var(--app-text-muted)] transition hover:bg-[color:var(--app-surface-muted)] active:scale-[0.98]"
            >
              {selectedTask.completedAt ? (
                <Undo2 aria-hidden="true" size={15} strokeWidth={2} />
              ) : (
                <Check aria-hidden="true" size={16} strokeWidth={2.2} />
              )}
            </button>
            {onClose && (
              <button
                type="button"
                data-testid="subtask-detail-close"
                aria-label="Close subtask details"
                onClick={onClose}
                className="flex h-6 w-6 items-center justify-center rounded text-[color:var(--app-text-muted)] transition hover:bg-[color:var(--app-surface-muted)] active:scale-[0.98]"
              >
                <X aria-hidden="true" size={15} strokeWidth={2.1} />
              </button>
            )}
            <button
              type="button"
              aria-label="More task actions"
              aria-expanded={actionsOpen}
              onClick={() => setActionsOpen((open) => !open)}
              className="flex h-6 w-6 items-center justify-center rounded text-[color:var(--app-text-muted)] transition hover:bg-[color:var(--app-surface-muted)] active:scale-[0.98]"
            >
              <MoreVertical aria-hidden="true" size={15} strokeWidth={2} />
            </button>
            {actionsOpen && (
              <div className="absolute right-0 top-7 z-10 w-40 overflow-hidden rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-panel)] py-1 shadow-xl">
                {selectedTask.trashedAt ? (
                  <button
                    type="button"
                    onClick={() => {
                      store.restoreTask(selectedTask.id);
                      setActionsOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--app-text)] hover:bg-[color:var(--app-surface-muted)]"
                  >
                    <Undo2 aria-hidden="true" size={15} />
                    Restore task
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      if (onDeleteTask) onDeleteTask(selectedTask.id);
                      else store.removeTask(selectedTask.id);
                      setActionsOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--app-danger)] hover:bg-[color:var(--app-danger-soft)]"
                  >
                    <Trash2 aria-hidden="true" size={15} />
                    Move to trash
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <dl className="task-detail-compact-fields mt-3 grid grid-cols-[4.25rem_minmax(0,1fr)] gap-x-2 gap-y-0 pl-10 text-[13px] leading-[18px]">
          <CompactDetailField
            label="scheduled"
            controlLabel="Scheduled"
            controlId={scheduledFieldId}
          >
            <SmartDateField
              id={scheduledFieldId}
              key={`${selectedTask.id}-scheduled`}
              value={selectedTask.scheduledAt}
              hasTime={selectedTask.scheduledAtHasTime}
              onCommit={(value, hasTime) =>
                store.updateTask(selectedTask.id, {
                  scheduledAt: value,
                  scheduledAtHasTime: hasTime,
                })
              }
            />
          </CompactDetailField>

          <CompactDetailField label="due" controlLabel="Due" controlId={dueFieldId}>
            <SmartDateField
              id={dueFieldId}
              key={`${selectedTask.id}-due`}
              value={selectedTask.dueAt}
              hasTime={selectedTask.dueAtHasTime}
              onCommit={(value, hasTime) =>
                store.updateTask(selectedTask.id, {
                  dueAt: value,
                  dueAtHasTime: hasTime,
                })
              }
            />
          </CompactDetailField>

          <CompactDetailField label="repeats">
            <SmartRepeatField
              key={`${selectedTask.id}-repeat`}
              value={selectedTask.recurrence ?? null}
              onChange={(recurrence) =>
                store.updateTask(selectedTask.id, { recurrence: recurrence ?? undefined })
              }
            />
          </CompactDetailField>

          <CompactDetailField label="estimate" controlId={`${selectedTask.id}-estimate-field`}>
            <SmartEstimateField
              id={`${selectedTask.id}-estimate-field`}
              key={`${selectedTask.id}-estimate`}
              value={selectedTask.estimateMinutes}
              onChange={(minutes) =>
                store.updateTask(selectedTask.id, { estimateMinutes: minutes })
              }
            />
          </CompactDetailField>

          <CompactDetailField
            label="project"
            controlId={`${selectedTask.id}-project-field`}
            extraTopSpace
          >
            <ProjectSearchField
              id={`${selectedTask.id}-project-field`}
              key={`${selectedTask.id}-project`}
              projects={projects}
              value={selectedTask.projectId}
              onChange={(nextProjectId) => {
                store.updateTask(selectedTask.id, { projectId: nextProjectId });
                store.setActiveProject(nextProjectId);
                if (currentView.kind === 'project' && currentView.projectId !== nextProjectId) {
                  onChangeView({ kind: 'project', projectId: nextProjectId });
                }
              }}
            />
          </CompactDetailField>

          <CompactDetailField label="workflow" controlId={`${selectedTask.id}-workflow-field`}>
            <WorkflowSelectField
              id={`${selectedTask.id}-workflow-field`}
              key={`${selectedTask.id}-workflow`}
              columns={workflowColumns}
              value={selectedTask.workflowState}
              onChange={(state) =>
                store.updateTask(selectedTask.id, {
                  workflowState: state as Task['workflowState'],
                })
              }
            />
          </CompactDetailField>

          <CompactDetailField label="tags" controlId={`${selectedTask.id}-tags-field`}>
            <TagsMultiSelectField
              id={`${selectedTask.id}-tags-field`}
              key={`${selectedTask.id}-tags`}
              tags={selectedTask.tags}
              allTags={allTagsWithCounts}
              onChange={(tags) => store.updateTask(selectedTask.id, { tags })}
            />
          </CompactDetailField>

          <CompactDetailField label="location" controlId={`${selectedTask.id}-location-field`}>
            <input
              id={`${selectedTask.id}-location-field`}
              key={`${selectedTask.id}-location`}
              aria-label="Location"
              defaultValue={selectedTask.location ?? ''}
              onBlur={(event) =>
                store.updateTask(selectedTask.id, {
                  location: event.target.value.trim() || undefined,
                })
              }
              onKeyDown={commitOnEnter}
              placeholder="none"
              className="w-full rounded border border-transparent bg-transparent px-1 py-0 text-sm text-[color:var(--app-text)] outline-none placeholder:text-[color:var(--app-text-soft)] hover:border-[color:var(--app-border)] focus:border-[color:var(--app-border-strong)] focus:bg-[color:var(--app-input-bg)]"
            />
          </CompactDetailField>

          <CompactDetailField label="url" controlId={`${selectedTask.id}-url-field`}>
            <UrlField
              id={`${selectedTask.id}-url-field`}
              key={`${selectedTask.id}-url`}
              value={selectedTask.url}
              onChange={(url) => store.updateTask(selectedTask.id, { url })}
            />
          </CompactDetailField>

          <CompactDetailField label="reminder" controlId={`${selectedTask.id}-reminder-field`}>
            <SmartDateField
              id={`${selectedTask.id}-reminder-field`}
              key={`${selectedTask.id}-reminder`}
              value={selectedTask.reminderAt}
              hasTime
              onCommit={(value) => store.updateTask(selectedTask.id, { reminderAt: value })}
            />
          </CompactDetailField>

          {optionalFields.has('energy') && (
            <CompactDetailField label="energy" controlId={`${selectedTask.id}-energy-field`}>
              <ChipSelectField
                id={`${selectedTask.id}-energy-field`}
                key={`${selectedTask.id}-energy`}
                value={selectedTask.energy ?? null}
                options={[
                  { value: null, label: 'none' },
                  { value: 'low', label: 'Low' },
                  { value: 'med', label: 'Med' },
                  { value: 'high', label: 'High' },
                ]}
                onChange={(v) =>
                  store.updateTask(selectedTask.id, {
                    energy: (v as EnergyT | null) ?? undefined,
                  })
                }
              />
            </CompactDetailField>
          )}

          {optionalFields.has('daypart') && (
            <CompactDetailField label="daypart" controlId={`${selectedTask.id}-daypart-field`}>
              <ChipSelectField
                id={`${selectedTask.id}-daypart-field`}
                key={`${selectedTask.id}-daypart`}
                value={selectedTask.daypart ?? null}
                options={[
                  { value: null, label: 'none' },
                  { value: 'morning', label: 'Morning' },
                  { value: 'afternoon', label: 'Afternoon' },
                  { value: 'evening', label: 'Evening' },
                  { value: 'night', label: 'Night' },
                ]}
                onChange={(v) =>
                  store.updateTask(selectedTask.id, {
                    daypart: (v as DaypartT | null) ?? undefined,
                  })
                }
              />
            </CompactDetailField>
          )}

          <div className="sr-only">
            <label htmlFor={`${selectedTask.id}-priority-field`}>Priority</label>
            <select
              id={`${selectedTask.id}-priority-field`}
              value={selectedTask.priority}
              onChange={(event) =>
                store.updateTask(selectedTask.id, {
                  priority: Number(event.target.value) as Task['priority'],
                })
              }
            >
              <option value={0}>None</option>
              <option value={1}>P1</option>
              <option value={2}>P2</option>
              <option value={3}>P3</option>
            </select>
          </div>

          <div className="relative col-start-2 pt-1">
            <button
              type="button"
              aria-expanded={addFieldOpen}
              onClick={() => setAddFieldOpen((o) => !o)}
              className="inline-flex h-7 items-center rounded border border-[color:var(--app-border)] bg-[color:var(--app-control-bg)] px-3 text-sm text-[color:var(--app-text)] transition hover:border-[color:var(--app-border-strong)] active:scale-[0.98]"
            >
              <Plus aria-hidden="true" size={13} className="mr-1" />
              Add field
            </button>
            {addFieldOpen && (
              <div className="absolute left-0 top-8 z-20 min-w-[10rem] overflow-hidden rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-panel)] py-1 shadow-xl">
                {(
                  [
                    { key: 'energy', label: 'Energy' },
                    { key: 'daypart', label: 'Daypart' },
                  ] as const
                ).map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setOptionalFields((prev) => {
                        const next = new Set(prev);
                        if (next.has(key)) next.delete(key);
                        else next.add(key);
                        return next;
                      });
                      setAddFieldOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--app-text)] hover:bg-[color:var(--app-surface-muted)]"
                  >
                    <span
                      className={`h-3.5 w-3.5 rounded border ${optionalFields.has(key) ? 'border-[color:var(--app-accent)] bg-[color:var(--app-accent)]' : 'border-[color:var(--app-border-strong)]'}`}
                    />
                    {label}
                    {optionalFields.has(key) && (
                      <span className="ml-auto text-xs text-[color:var(--app-text-soft)]">
                        shown
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </dl>
      </section>

      <ScrollViewport
        key={`${selectedTask.id}-${selectedTask.updatedAt}`}
        data-testid="task-detail-secondary-scroll"
        className="min-h-0 flex-1"
        viewportClassName="task-detail-secondary-viewport h-full overflow-x-hidden"
        showIndicator
      >
        <div className="pb-8">
          <section className="pane-divider-t">
            <div className="grid min-h-10 grid-cols-[minmax(0,1fr)_auto] items-end">
              <h3 className="px-2 pb-1 text-lg font-normal leading-8 text-[color:var(--app-text)]">
                Subtasks
              </h3>
              <div className="flex items-end pr-2 text-sm">
                <button
                  type="button"
                  aria-pressed={subtaskTab === 'incomplete'}
                  onClick={() => setSubtaskTab('incomplete')}
                  className={`border border-b-0 px-3 py-1.5 ${
                    subtaskTab === 'incomplete'
                      ? 'border-[color:var(--app-border)] bg-[color:var(--app-workspace)] font-semibold text-[color:var(--app-text)]'
                      : 'border-transparent text-[color:var(--app-text-soft)]'
                  }`}
                >
                  Incomplete
                </button>
                <button
                  type="button"
                  aria-pressed={subtaskTab === 'completed'}
                  onClick={() => setSubtaskTab('completed')}
                  className={`border border-b-0 px-3 py-1.5 ${
                    subtaskTab === 'completed'
                      ? 'border-[color:var(--app-border)] bg-[color:var(--app-workspace)] font-semibold text-[color:var(--app-text)]'
                      : 'border-transparent text-[color:var(--app-text-soft)]'
                  }`}
                >
                  Completed
                </button>
                <button
                  type="button"
                  aria-label="Subtask settings"
                  className="flex h-8 w-8 items-center justify-center text-[color:var(--app-text-soft)]"
                >
                  <MoreVertical aria-hidden="true" size={16} />
                </button>
              </div>
            </div>

            {selectedTask.parentTaskId === undefined && (
              <form
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  const child = store.createChildTask(selectedTask.id, childTitle);
                  if (child) setChildTitle('');
                }}
                className="pane-divider-t px-2 py-2"
              >
                <input
                  ref={childInputRef}
                  id={`${selectedTask.id}-child-field`}
                  value={childTitle}
                  onChange={(event) => setChildTitle(event.target.value)}
                  placeholder="Add child task"
                  className="h-8 w-full rounded border border-[color:var(--app-border)] bg-[color:var(--app-input-bg)] px-2 text-sm outline-none placeholder:text-[color:var(--app-text-soft)] focus:border-[color:var(--app-border-strong)]"
                />
                {childTitle.trim() !== '' && (
                  <div
                    className="mt-2 flex flex-wrap gap-1.5"
                    aria-label="Child task Smart Add fields"
                  >
                    <ChildSmartAddButton
                      label="Due today"
                      token="^today"
                      onClick={appendChildToken}
                      icon={<CalendarPlus aria-hidden="true" size={14} />}
                    />
                    <ChildSmartAddButton
                      label="Start tomorrow"
                      token="~tomorrow"
                      onClick={appendChildToken}
                      icon={<Clock3 aria-hidden="true" size={14} />}
                    />
                    <ChildSmartAddButton
                      label="Priority 1"
                      token="!1"
                      onClick={appendChildToken}
                      icon={<ChevronRight aria-hidden="true" size={14} />}
                    />
                    <ChildSmartAddButton
                      label="Tag"
                      token="#"
                      onClick={appendChildToken}
                      icon={<Tags aria-hidden="true" size={14} />}
                    />
                    <ChildSmartAddButton
                      label="Repeat weekly"
                      token="*weekly"
                      onClick={appendChildToken}
                      icon={<Repeat2 aria-hidden="true" size={14} />}
                    />
                  </div>
                )}
              </form>
            )}

            {visibleChildTasks.length === 0 && subtaskTab === 'completed' ? (
              <div className="pane-divider-t px-2 py-3 text-sm text-[color:var(--app-text-soft)]">
                No completed subtasks.
              </div>
            ) : visibleChildTasks.length > 0 ? (
              <ul className="pane-divider-t pane-divide-y">
                {visibleChildTasks.map((child) => (
                  <li
                    key={child.id}
                    className="grid min-h-9 grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-2 px-2"
                  >
                    <button
                      type="button"
                      aria-label={child.completedAt ? 'Reopen child task' : 'Complete child task'}
                      onClick={() => store.toggleTaskComplete(child.id)}
                      className="flex h-7 w-7 items-center justify-center rounded text-[color:var(--app-text-soft)] transition hover:bg-[color:var(--app-surface-muted)] active:scale-[0.98]"
                    >
                      {child.completedAt ? (
                        <CheckCircle2 aria-hidden="true" size={16} />
                      ) : (
                        <ListTree aria-hidden="true" size={15} />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => onSelectChildTask?.(child.id)}
                      disabled={!onSelectChildTask}
                      className={`min-w-0 truncate text-left text-sm outline-none transition hover:text-[color:var(--app-accent-text)] focus-visible:ring-2 focus-visible:ring-[rgba(185,108,45,0.3)] disabled:pointer-events-none disabled:hover:text-inherit ${
                        child.completedAt
                          ? 'text-[color:var(--app-text-soft)] line-through'
                          : 'text-[color:var(--app-text)]'
                      }`}
                    >
                      {child.title}
                    </button>
                    <span className="shrink-0 text-xs text-[color:var(--app-text-soft)]">
                      {formatTaskAnchorLabel(child)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>

          <section className="pane-divider-t">
            <div className="flex items-center justify-between gap-2 px-2 py-2">
              <h3 className="text-lg font-normal leading-8 text-[color:var(--app-text)]">Notes</h3>
              {!editingNotes && (
                <div className="flex items-center gap-1">
                  {onOpenNotesWorkspace && (
                    <button
                      type="button"
                      onClick={() => onOpenNotesWorkspace(selectedTask.id)}
                      className="ui-button ui-button-subtle h-8 gap-1 px-2 text-xs"
                    >
                      <Maximize2 aria-hidden="true" size={13} />
                      Open workspace
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setNotesDraft(selectedTask.notes ?? '');
                      setEditingNotes(true);
                    }}
                    className="ui-button ui-button-subtle h-8 gap-1 px-2 text-xs"
                  >
                    <Pencil aria-hidden="true" size={13} />
                    {hasNotes ? 'Edit notes' : 'Add notes'}
                  </button>
                </div>
              )}
            </div>
            {editingNotes ? (
              <TaskNotesEditor
                fieldId={notesFieldId}
                draft={notesDraft}
                onChange={setNotesDraft}
                onCancel={() => {
                  setNotesDraft(selectedTask.notes ?? '');
                  setEditingNotes(false);
                }}
                onSave={saveNotes}
              />
            ) : hasNotes ? (
              <TaskNotesMarkdown notes={selectedTask.notes ?? ''} className="px-2 pb-3" />
            ) : (
              <p className="px-2 pb-3 text-sm text-[color:var(--app-text-soft)]">No notes yet.</p>
            )}
            <TaskActivityThread
              activities={visibleActivities}
              note={activityNote}
              noteId={`${selectedTask.id}-activity-note`}
              onNoteChange={setActivityNote}
              onSubmit={(event) => {
                event.preventDefault();
                const created = store.createTaskActivity({
                  taskId: selectedTask.id,
                  type: 'comment',
                  message: activityNote,
                });
                if (created) setActivityNote('');
              }}
              onDeleteActivity={(id) => store.deleteTaskActivity(id)}
              onUpdateActivity={(id, message) => store.updateTaskActivity(id, message)}
            />
          </section>

          <TaskPlanningData
            task={selectedTask}
            tasks={snapshot.tasks}
            objectives={snapshot.objectives}
            initiatives={prioritization.getInitiatives()}
            evidence={taskEvidence}
          />

          <TaskAuditHistory activities={activities} enrichmentEvents={taskEnrichmentEvents} />
        </div>
      </ScrollViewport>
    </aside>
  );
}

function TaskNotesEditor({
  fieldId,
  draft,
  onChange,
  onCancel,
  onSave,
}: {
  fieldId: string;
  draft: string;
  onChange(value: string): void;
  onCancel(): void;
  onSave(): void;
}): JSX.Element {
  return (
    <div className="space-y-2">
      <label htmlFor={fieldId} className="sr-only">
        Notes
      </label>
      <textarea
        id={fieldId}
        value={draft}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Task notes (Markdown supported)"
        className="block min-h-28 w-full resize-y rounded border border-[color:var(--app-border)] bg-transparent px-2 py-1.5 text-sm leading-6 text-[color:var(--app-text)] outline-none placeholder:text-[color:var(--app-text-soft)] focus-visible:ring-2 focus-visible:ring-[color:var(--app-accent)]"
      />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="ui-button ui-button-subtle">
          Cancel
        </button>
        <button type="button" onClick={onSave} className="ui-button ui-button-primary">
          Save notes
        </button>
      </div>
    </div>
  );
}

export function TaskNotesMarkdown({
  notes,
  className,
}: { notes: string; className: string }): JSX.Element {
  return (
    <div
      data-testid="task-notes-preview"
      className={`task-notes-markdown text-sm leading-6 ${className}`}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
        {notes}
      </ReactMarkdown>
    </div>
  );
}

const MARKDOWN_COMPONENTS: Components = {
  h1: ({ children }) => <h4 className="mb-2 mt-4 text-lg font-semibold first:mt-0">{children}</h4>,
  h2: ({ children }) => (
    <h5 className="mb-2 mt-4 text-base font-semibold first:mt-0">{children}</h5>
  ),
  h3: ({ children }) => <h6 className="mb-1 mt-3 font-semibold first:mt-0">{children}</h6>,
  p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="mb-3 list-disc space-y-1 pl-5 last:mb-0">{children}</ul>,
  ol: ({ children }) => <ol className="mb-3 list-decimal space-y-1 pl-5 last:mb-0">{children}</ol>,
  blockquote: ({ children }) => (
    <blockquote className="mb-3 border-l-2 border-[color:var(--app-border-strong)] pl-3 text-[color:var(--app-text-muted)]">
      {children}
    </blockquote>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-[color:var(--app-accent-text)] underline underline-offset-2"
    >
      {children}
    </a>
  ),
  code: ({ children }) => (
    <code className="rounded bg-[color:var(--app-surface-muted)] px-1 py-0.5 font-mono text-[0.85em]">
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="mb-3 overflow-x-auto rounded bg-[color:var(--app-surface-muted)] p-3 text-xs">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="mb-3 overflow-x-auto">
      <table className="w-full border-collapse text-left">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-[color:var(--app-border)] bg-[color:var(--app-surface-muted)] px-2 py-1 font-medium">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-[color:var(--app-border)] px-2 py-1 align-top">{children}</td>
  ),
};

function CompactDetailField({
  label,
  controlLabel,
  controlId,
  extraTopSpace,
  children,
}: {
  label: string;
  controlLabel?: string;
  controlId?: string;
  extraTopSpace?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <>
      <dt className={`text-[color:var(--app-text-soft)]${extraTopSpace ? ' mt-0.5' : ''}`}>
        {controlId ? (
          <>
            <label htmlFor={controlId} className="sr-only">
              {controlLabel ?? label}
            </label>
            <span aria-hidden="true">{label}</span>
          </>
        ) : (
          label
        )}
      </dt>
      <dd
        className={`min-w-0 break-words [overflow-wrap:anywhere] text-[color:var(--app-text)]${extraTopSpace ? ' mt-0.5' : ''}`}
      >
        {children}
      </dd>
    </>
  );
}

function ChildSmartAddButton({
  label,
  token,
  icon,
  onClick,
}: {
  label: string;
  token: string;
  icon: ReactNode;
  onClick(token: string): void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={() => onClick(token)}
      className="inline-flex h-8 min-w-8 items-center justify-center rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-control-bg)] px-2 text-[color:var(--app-text-muted)] transition hover:border-[color:var(--app-border-strong)] hover:text-[color:var(--app-text)] active:scale-[0.98]"
    >
      {icon}
    </button>
  );
}
